// Copyright 2025, Imprivata, Inc.  All rights reserved.

import type { TFunction } from 'i18next';
import type { FunctionComponent, SVGProps } from 'react';

import { FactorType } from '@api/types';
import ApplicationsIcon from '../../assets/icons/applications.svg?react';
import FaceBioImg from '../../assets/icons/authenticators/face_bio.svg?react';
import IdImg from '../../assets/icons/authenticators/imprivata_ID.svg?react';
import OtpImg from '../../assets/icons/authenticators/otp.svg?react';
import PasswordImg from '../../assets/icons/authenticators/password.svg?react';
import PinImg from '../../assets/icons/authenticators/pin.svg?react';
import ProxCardImg from '../../assets/icons/authenticators/prox_card.svg?react';
import TempCodeImg from '../../assets/icons/authenticators/temp_code.svg?react';
import UsernameImg from '../../assets/icons/authenticators/username.svg?react';

const TRANSLATION_ROOT = 'self-service.authenticator-list.auth-method-info';

// The project's own authenticator artwork, keyed by factor. Sizing stays with the
// consumers' CSS (`.authenticator-name-and-icon svg`, `.list-card-icon svg`),
// since the assets' intrinsic boxes differ (24x24 through 32x32).
type FactorIcon = FunctionComponent<SVGProps<SVGSVGElement>>;

const FACTOR_ICONS: Partial<Record<FactorType, FactorIcon>> = {
  [FactorType.FACE]: FaceBioImg,
  [FactorType.PROX]: ProxCardImg,
  [FactorType.PASSWORD]: PasswordImg,
  [FactorType.PIN]: PinImg,
  [FactorType.USERNAME]: UsernameImg,
  [FactorType.TEMP_CODE]: TempCodeImg,
  [FactorType.OTP]: OtpImg,
  [FactorType.IMPRIVATA_ID]: IdImg,
  [FactorType.LEARNED_CREDS]: ApplicationsIcon,
};

const FACTOR_NAME_KEYS: Partial<Record<FactorType, string>> = {
  [FactorType.FACE]: 'face-recognition',
  [FactorType.PROX]: 'proximity-card',
  // Sonar's hard-coded-credential hotspot (S2068) fires on any string literal
  // assigned under a `password`-named key. This is a translation key, so the
  // hotspot is suppressed where it is raised rather than worked around by
  // renaming either the factor or its key.
  [FactorType.PASSWORD]: 'password', // NOSONAR
  [FactorType.PIN]: 'pin',
  [FactorType.USERNAME]: 'username',
  [FactorType.TEMP_CODE]: 'temporary-code',
  [FactorType.OTP]: 'otp',
  [FactorType.IMPRIVATA_ID]: 'imprivata-id',
  [FactorType.LEARNED_CREDS]: 'learned-credentials',
};

const mapFactorTypeToNameAndIcon = (factorType: FactorType, t: TFunction) => {
  // Unknown factors fall back to the generic identity icon and their raw type.
  const FactorIcon = FACTOR_ICONS[factorType] ?? UsernameImg;
  const nameKey = FACTOR_NAME_KEYS[factorType];

  return {
    name: nameKey ? t(`${TRANSLATION_ROOT}.${nameKey}`) : factorType,
    // The name always accompanies the icon, so it carries no extra meaning.
    icon: <FactorIcon aria-hidden="true" />,
  };
};

export default mapFactorTypeToNameAndIcon;
